import { initLegacyFaq } from './faq.js';
import { loadConverterHighstock } from './header-charts.js';

function afterPaint(callback) {
  requestAnimationFrame(() => window.setTimeout(callback, 0));
}

function amountUrl(state, baseUrl) {
  const url = new URL(baseUrl, window.location.origin);
  const device = new URL(window.location.href).searchParams.get('arz-device');
  if (url.pathname === '/convert/') {
    url.searchParams.set('b', state.base.slug);
    url.searchParams.set('q', state.quote.slug);
  }
  if (Number(state.baseAmount) !== 1) url.searchParams.set('amount', String(state.baseAmount));
  else url.searchParams.delete('amount');
  if (device) url.searchParams.set('arz-device', device);
  return `${url.pathname}${url.search}`;
}

export function updateAmountUrl(state) {
  if (state.mode !== 'convert') return;
  window.history.replaceState({}, '', amountUrl(state, state.pairUrl));
}

export function scheduleAmountUrlUpdate(state) {
  window.clearTimeout(state.amountUrlTimer);
  if (state.mode !== 'convert') return;
  state.amountUrlTimer = window.setTimeout(() => {
    state.amountUrlTimer = 0;
    updateAmountUrl(state);
  }, 300);
}

function replaceHtml(selector, html) {
  if (!html) return null;
  const current = document.querySelector(selector);
  if (!current) return null;
  const template = document.createElement('template');
  template.innerHTML = html.trim();
  const next = template.content.firstElementChild;
  if (!next) return null;
  current.replaceWith(next);
  return next;
}

function updateMetadata(payload) {
  document.title = payload.title;
  const description = document.querySelector('meta[name="description"]');
  if (description) description.setAttribute('content', payload.description);
  const heading = document.querySelector('.arz-coin-page-title a, .arz-coin-page-title');
  if (heading) heading.textContent = payload.heading;
  const crumbs = document.querySelectorAll('.arz-path-list .arz-path-text');
  if (crumbs.length) crumbs[crumbs.length - 1].textContent = payload.breadcrumb;
}

const scriptLoads = new Map();

function loadScriptOnce(src, isReady = () => true) {
  if (isReady()) return Promise.resolve();
  if (scriptLoads.has(src)) return scriptLoads.get(src);
  const load = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src^="${src}"]`);
    const script = existing || document.createElement('script');
    const finish = () => isReady()
      ? resolve()
      : reject(new Error(`Script loaded without initializing: ${src}`));
    script.addEventListener('load', finish, { once: true });
    script.addEventListener('error', reject, { once: true });
    // A second converter refresh can find a script whose load event has
    // already fired. Do not wait forever for an event that cannot recur.
    if (existing) {
      window.setTimeout(() => {
        if (isReady()) resolve();
      }, 0);
      return;
    }
    script.src = src;
    script.async = true;
    document.head.append(script);
  }).catch((error) => {
    scriptLoads.delete(src);
    throw error;
  });
  scriptLoads.set(src, load);
  return load;
}

function updateChartHeading(container, state) {
  ['base', 'quote'].forEach((side) => {
    const asset = state[side];
    const name = asset?.fa_name || asset?.name || '';
    const node = container.querySelector(`[data-av-value="faname-${side}"]`);
    if (node && name) node.textContent = name;
  });
}

function prepareChart(state, chart) {
  const container = document.querySelector('[data-converter-fiat-chart]');
  if (!container) return;
  container.hidden = !chart?.enabled;
  if (!chart?.enabled) {
    container.dataset.requestedPair = '';
    return;
  }
  updateChartHeading(container, state);
  const pairKey = `${chart.baseId}:${chart.quoteId}`;
  container.dataset.requestedPair = pairKey;
  const load = async () => {
    if (container.dataset.requestedPair !== pairKey || container.dataset.loadedPair === pairKey) return;
    const theme = state.root.dataset.converterTheme || '';
    try {
      delete container.dataset.chartError;
      await loadConverterHighstock(`${theme}/assets/js/libs/highstock.js`);
      await loadScriptOnce(
        state.root.dataset.converterFiatChartSrc || `${theme}/assets/js/chart-fiat2fiat.js`,
        () => typeof window.fiat2fiat_changeChart === 'function',
      );
      if (container.dataset.requestedPair !== pairKey) return;
      if (typeof window.fiat2fiat_changeChart === 'function') {
        window.fiat2fiat_changeChart({ baseid: chart.baseId, quoteid: chart.quoteId, chartHeight: 300, container: 'fiatFiatChart', seriesName: chart.seriesName }, '1y');
        container.dataset.loadedPair = pairKey;
      }
    } catch (error) {
      if (container.dataset.requestedPair === pairKey) {
        delete container.dataset.loadedPair;
        container.dataset.chartError = error instanceof Error ? error.message : 'Chart failed to load';
      }
    }
  };
  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) { observer.disconnect(); load(); }
    }, { rootMargin: '300px' });
    observer.observe(container);
  } else afterPaint(load);
}

export function schedulePairContent(state) {
  window.clearTimeout(state.amountUrlTimer);
  state.amountUrlTimer = 0;
  if (state.mode !== 'convert') return;
  afterPaint(async () => {
    const key = `${state.base.slug}:${state.quote.slug}`;
    const requestId = ++state.pairRequestId;
    state.pairController?.abort();
    if (state.pairCache.has(key)) {
      applyPairContent(state, state.pairCache.get(key));
      return;
    }
    const controller = new AbortController();
    state.pairController = controller;
    const endpoint = new URL(state.root.dataset.converterEndpoint, window.location.origin);
    endpoint.searchParams.set('base', state.base.slug);
    endpoint.searchParams.set('quote', state.quote.slug);
    try {
      const response = await fetch(endpoint, { signal: controller.signal, headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(`Pair content failed: ${response.status}`);
      const payload = await response.json();
      if (requestId !== state.pairRequestId) return;
      state.pairCache.set(key, payload);
      applyPairContent(state, payload);
    } catch (error) {
      if (error.name !== 'AbortError' && requestId === state.pairRequestId) state.root.dataset.converterContentError = '1';
    } finally {
      if (requestId === state.pairRequestId) state.pairController = null;
    }
  });
}

function applyPairContent(state, payload) {
  delete state.root.dataset.converterContentError;
  state.pairUrl = payload.url;
  window.history.replaceState({}, '', amountUrl(state, payload.url));
  updateMetadata(payload);
  replaceHtml('[data-converter-rate-tables], .changeRatesTable', payload.rateTablesHtml);
  replaceHtml('[data-converter-popular-pairs]', payload.popularPairsHtml);
  const content = `${payload.contentHtml || ''}${payload.faqHtml || ''}`;
  const pairContent = replaceHtml('[data-converter-pair-content], .extracontent', `<div class="extracontent arz-tw-px-4 md:arz-tw-px-0" data-converter-pair-content>${content}</div>`);
  initLegacyFaq(pairContent);
  prepareChart(state, payload.chart);
}
